import { useRef, useEffect, useMemo } from "react";
import { ChatMessage } from "@/zapi-frontend/shared/ui/custom/ChatMessage";
import { ChatMessage as ChatMessageType } from "@/zapi-frontend/stores/chatStore";
import { Typography, LoadingSpinner } from "@libs/zapi-ui/custom";

interface ChatMessageListProps {
  messages: ChatMessageType[];
  loadingMessageId: string | null;
  isGeneratingChapter: boolean;
  progressMessage?: string | null;
  prereqsChaptersToBeGenerated: string[] | null;
  isInitializing: boolean;
}

const hashMessages = (messages: ChatMessageType[]) => {
  return messages.map((msg) => msg.id).join("-");
};

export function ChatMessageList({
  messages,
  loadingMessageId,
  isGeneratingChapter,
  progressMessage,
  prereqsChaptersToBeGenerated,
  isInitializing
}: ChatMessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const messagesHash = useMemo(() => hashMessages(messages), [messages]);
  useEffect(() => {
    const timeout = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);

    return () => clearTimeout(timeout);
  }, [messagesHash, loadingMessageId, progressMessage, prereqsChaptersToBeGenerated]);

  if (isInitializing) {
    return (
      <div className="h-full flex gap-2 items-center justify-center">
        <LoadingSpinner />
        <Typography variant="body2" color="zinc600">
          Loading Messages...
        </Typography>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-4 py-4">
      <div className="space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex items-start ${message.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <ChatMessage
              message={message}
              isLoading={message.id === loadingMessageId}
              progressMessage={message.id === loadingMessageId && isGeneratingChapter ? progressMessage : undefined}
            />
          </div>
        ))}
        {loadingMessageId &&
          isGeneratingChapter &&
          prereqsChaptersToBeGenerated &&
          prereqsChaptersToBeGenerated.length > 0 && (
            <div className="flex items-start justify-start">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 max-w-[80%]">
                <Typography variant="body2" className="text-gray-700">
                  We will generate the following prerequisite chapters as well:{" "}
                  <strong>{prereqsChaptersToBeGenerated.join(", ")}</strong>
                </Typography>
              </div>
            </div>
          )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}
